import { queriesEngagements, queriesRequirementDefinitions } from "@query";
import {
  WSChip,
  WSElement,
  WSEmptyState,
  WSFlexBox,
  WSPage,
  WSPageToolbar,
  WSText
} from "@wingspanhq/fe-component-library";
import {
  RequirementDefinition,
  RequirementDefinitionStatus
} from "@wingspanhq/wingspan-api-sdk";
import orderBy from "lodash/orderBy";
import React from "react";
import { Route, Switch, useHistory } from "react-router-dom";

import { Tabs } from "../../../../components/Tabs";
import { WSQueries } from "../../../../query/WSQuery";
import { useBrowserPageTitle } from "../../../../shared/hooks/useBrowserPageTitle";
import { WS_LINKS } from "../../../../types/wsLinks";
import { openInNewTab } from "../../../../utils/openInNewTab";
import { RequirementDefinitionsTable } from "../../components/RequirementsTable";
import {
  getCreateRequirementDefinitionPathname,
  getSectionPathByRequirementStatus
} from "../../paths/helpers";
import { ENGAGEMENT_REQUIREMENT_TYPES } from "../../types";

import styles from "./styles.module.scss";

const selectorRequirementDefinitionsByStatus = (
  requirementDefinitions: RequirementDefinition[],
  status: RequirementDefinitionStatus
) =>
  orderBy(
    requirementDefinitions.filter(
      requirementDefinition => requirementDefinition.status === status
    ),
    requirementDefinition => requirementDefinition.events?.updatedAt ?? 0,
    "desc"
  );

const RequirementDefinitionsTab: React.FC<{
  requirementDefinitions: RequirementDefinition[];
  listPath: string;
  isMainTab?: boolean;
}> = props => {
  const history = useHistory();

  const engagementsListAllQuery =
    queriesEngagements.listEngagements.useQueryAll(undefined, {
      refetchOnMount: false,
      retry: false
    });

  return (
    <WSQueries queries={{ engagementsListAllQuery }}>
      {() => (
        <>
          <WSFlexBox.CenterY className={styles.statsContainer} mb="L">
            <WSElement className={styles.resultsStats}>
              <WSText.ParagraphXs color="gray500">Showing:</WSText.ParagraphXs>
              <WSChip
                pillText={`${props.requirementDefinitions.length} items`}
                pillType="noBackground"
                theme="neutral"
              />
            </WSElement>
          </WSFlexBox.CenterY>
          {props.requirementDefinitions.length === 0 ? (
            <WSEmptyState
              actions={
                props.isMainTab
                  ? {
                      buttons: [
                        {
                          label: "Create requirement",
                          kind: "Primary",
                          onClick: () => {
                            history.push(
                              getCreateRequirementDefinitionPathname()
                            );
                          }
                        },
                        {
                          label: "Learn about requirements",
                          kind: "Secondary",
                          onClick: () =>
                            openInNewTab(WS_LINKS.eligibilityRequirements)
                        }
                      ]
                    }
                  : undefined
              }
              bgColor="gray"
              description={
                props.isMainTab
                  ? "Once you create requirements, they will appear here."
                  : undefined
              }
              hasBorder
              py="3XL"
              title="No requirements found"
              type="documents"
            />
          ) : (
            <RequirementDefinitionsTable
              engagements={engagementsListAllQuery.flatData ?? []}
              listPath={props.listPath}
              requirements={props.requirementDefinitions}
            />
          )}
        </>
      )}
    </WSQueries>
  );
};

export const RouteRequirements: React.FC = () => {
  const history = useHistory();
  useBrowserPageTitle("Engagement requirements");

  // Status, sort and v1's `notIn` type exclusion have no v3 filter, so the
  // whole catalogue is read once and split into the two tabs client-side.
  // The general-type exclusion applies to the Active tab only — v1 kept the
  // Archived tab unfiltered by type, so archived general-type definitions
  // must stay visible there.
  const queryRequirementDefinitions =
    queriesRequirementDefinitions.listRequirementDefinitions.useQueryAll();

  return (
    <WSPage>
      <WSPageToolbar
        actions={[
          {
            label: "Create requirement",
            onClick: async () => {
              history.push(getCreateRequirementDefinitionPathname());
            },
            buttonKind: "Primary"
          }
        ]}
        title={"Engagement requirements"}
      >
        <WSText.ParagraphSm color="gray500" style={{ maxWidth: "550px" }}>
          Engagement requirements can be added to engagements. Contractors must
          complete these requirements to be eligible for payment for those
          engagements.
        </WSText.ParagraphSm>
      </WSPageToolbar>

      <WSQueries queries={{ queryRequirementDefinitions }}>
        {() => {
          const requirementDefinitions =
            queryRequirementDefinitions.flatData ?? [];
          const engagementRequirementDefinitions =
            requirementDefinitions.filter(
              requirementDefinition =>
                !!requirementDefinition.type &&
                ENGAGEMENT_REQUIREMENT_TYPES.includes(
                  requirementDefinition.type
                )
            );

          const tabs = [
            {
              label: "Active",
              path: getSectionPathByRequirementStatus(
                RequirementDefinitionStatus.ACTIVE
              ),
              requirementDefinitions: selectorRequirementDefinitionsByStatus(
                engagementRequirementDefinitions,
                RequirementDefinitionStatus.ACTIVE
              ),
              isMainTab: true
            },
            {
              label: "Archived",
              path: getSectionPathByRequirementStatus(
                RequirementDefinitionStatus.INACTIVE
              ),
              requirementDefinitions: selectorRequirementDefinitionsByStatus(
                requirementDefinitions,
                RequirementDefinitionStatus.INACTIVE
              )
            }
          ];

          return (
            <>
              <Tabs
                mb="L"
                tabs={tabs.map(tab => ({
                  ...tab,
                  badge: tab.requirementDefinitions.length
                }))}
              />
              <Switch>
                {tabs.map(tab => (
                  <Route
                    key={tab.path}
                    path={tab.path}
                    render={() => (
                      <RequirementDefinitionsTab
                        isMainTab={tab.isMainTab}
                        listPath={tab.path}
                        requirementDefinitions={tab.requirementDefinitions}
                      />
                    )}
                  />
                ))}
              </Switch>
            </>
          );
        }}
      </WSQueries>
    </WSPage>
  );
};
